import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Store } from 'types/store';
import { VehicleFormModel, VehicleType } from 'types/common/vehicles';
import { addRLRVehicle, setChosenVehicle } from 'actions/journeys/loyaltyRewardsActions';
import { isEmpty } from 'utils/utils_validation';
import { regNoRegex } from 'utils/vehicleHelpers';
import { ResponseCode } from 'services/models/httpResponse';
import { FindVehicle } from 'services/VehicleService';

interface FormState {
  error: string | null;
  isValidRegistration: boolean;
}

type ApiError = string | { responseCode: string }

function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === 'object'
    && error !== null
    && 'responseCode' in error
    && typeof error.responseCode === 'string'
    && error.responseCode === 'error'
  );
}

const useVehicleSearch = (toggleVehicleModal: React.Dispatch<React.SetStateAction<boolean>>) => {
  const dispatch = useDispatch();
  const vehicles = useSelector((state: Store) => state.journeys.loyaltyRewards.vehicles);

  const [loading, setLoading] = useState(false);
  const [inputValues, setInputValues] = useState<VehicleFormModel>({
    Registration: '',
    YearOfManufacture: '',
    Make: '',
    Model: '',
    Transmission: 'Manual',
    FuelType: 'Petrol',
  });

  const [formState, setFormState] = useState<FormState>({
    error: null,
    isValidRegistration: false,
  });

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setInputValues((prevState) => ({ ...prevState, [name]: value }));
  };

  const findVehicle = async (regNo: string) => {
    if (!isEmpty(regNo) && regNoRegex.test(regNo)) {
      setLoading(true);
      setFormState({ error: null, isValidRegistration: true });

      try {
        const vehicleData = await FindVehicle({ Registration: regNo });
        const normalisedRegNo = regNo.replace(/\s/g, '').toUpperCase();

        if (vehicleData.responseCode === ResponseCode.SUCCESS) {
          if (!vehicleData.payload) {
            setInputValues({
              ...vehicleData.payload,
              Registration: normalisedRegNo,
            });
            setFormState({ error: 'Sorry, we couldn\'t find a vehicle with the given number plate. Please try again or enter details manually below', isValidRegistration: false });
            toggleVehicleModal(true);
          } else {
            setInputValues({
              ...vehicleData.payload,
              Registration: normalisedRegNo,
            });
            setFormState({ error: null, isValidRegistration: true });
            toggleVehicleModal(true);
          }
        }
      } catch (error: unknown) {
        const errorMsg = isApiError(error)
          ? 'Sorry, we couldn\'t find a vehicle with the given number plate. Please try again or enter details manually below'
          : 'We were unable to complete your request - please try again later.';
        setFormState({ error: errorMsg, isValidRegistration: false });
      } finally {
        setLoading(false);
      }
    } else {
      setFormState({
        error: 'Please enter valid registration',
        isValidRegistration: false,
      });
    }
  };

  const saveVehicle = (vehicleToSave: VehicleType) => {
    const isValidYear = vehicleToSave.yearOfManufacture > 999; // Checking if year "number" is at least 4 characters
    const normalisedReg = vehicleToSave.registration.replace(/\s/g, '').toUpperCase();
    const regCheck = vehicles.some((vehicle) => vehicle.registration === normalisedReg);
    const isFormComplete = Object.values(vehicleToSave).every(Boolean) && isValidYear;

    if (isFormComplete && !regCheck && formState.isValidRegistration) {
      dispatch(addRLRVehicle(vehicleToSave));
      dispatch(setChosenVehicle(vehicleToSave));
      setInputValues({
        Registration: '',
        YearOfManufacture: '',
        Make: '',
        Model: '',
        Transmission: 'Manual',
        FuelType: 'Petrol',
      });
      toggleVehicleModal(false);
      setFormState({ error: null, isValidRegistration: true });
    } else {
      let errorMsg = '';
      if (regCheck) {
        errorMsg = 'Vehicle already added';
      } else if (!Object.values(vehicleToSave).every(Boolean)) {
        errorMsg = 'Please complete all required fields';
      } else if (!isValidYear) {
        errorMsg = 'Please enter 4 digits year of manufacture';
      }

      setFormState({
        error: errorMsg,
        isValidRegistration: true,
      });
    }
  };

  return {
    loading,
    findVehicle,
    saveVehicle,
    inputValues,
    handleInputChange,
    setInputValues,
    formState,
    setFormState,
  };
};

export default useVehicleSearch;
